import React, { useState, useEffect } from 'react';
import {
  FileText,
  Edit,
  Users,
  Globe,
  ArrowRight,
  Layers,
  CheckCircle2,
  Cpu,
  ShieldCheck,
  Terminal,
  Activity,
  Code2,
  Zap,
  Lock,
  Database,
  ShoppingBag,
  Paintbrush,
  Puzzle,
  Sliders,
  RefreshCw,
  Search,
  Building2,
  Server,
  Check,
  Sparkles,
  Award,
  Settings,
  HeartHandshake,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';

const CMSSolution = () => {
  const [activeEcommerceTab, setActiveEcommerceTab] = useState('catalog');
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Our WordPress & WooCommerce Development Services (9 Core Pillars)
  const servicesList = [
    {
      icon: Building2,
      title: 'Custom Business Websites',
      badge: 'Corporate & Brand',
      borderGradient: 'from-cyan-400 via-sky-500 via-blue-600 to-cyan-400',
      iconColor: 'from-blue-600 to-cyan-500',
      shadowColor: 'shadow-cyan-500/15',
      description: 'Professional, responsive websites designed to showcase your brand, services, and expertise while providing an exceptional user experience across all devices.',
      features: ['Mobile-First Responsive UI', 'Custom Branding & UX', 'High-Converting Layouts']
    },
    {
      icon: ShoppingBag,
      title: 'WooCommerce E-Commerce Development',
      badge: 'High Conversion',
      borderGradient: 'from-amber-400 via-orange-500 via-red-500 to-amber-400',
      iconColor: 'from-orange-500 to-amber-500',
      shadowColor: 'shadow-orange-500/15',
      description: 'Launch a feature-rich online store with secure payment gateways, product management, inventory tracking, shipping integration, coupons, tax configuration, and order management.',
      features: ['Stripe, PayPal & Razorpay Sync', 'Inventory & Stock Automation', 'Bespoke Cart & Checkout UI']
    },
    {
      icon: Paintbrush,
      title: 'Custom Theme Design & Development',
      badge: 'Zero Template Bloat',
      borderGradient: 'from-purple-500 via-pink-500 via-rose-500 to-purple-500',
      iconColor: 'from-purple-600 to-pink-500',
      shadowColor: 'shadow-purple-500/15',
      description: 'Stand out with a unique website designed specifically for your brand instead of relying on generic templates, built with native Gutenberg custom blocks.',
      features: ['Pixel-Perfect Figma to WP', 'Native Block & FSE Architecture', 'Lightweight Codebase']
    },
    {
      icon: Puzzle,
      title: 'Plugin Development & Integration',
      badge: 'PHP 8.3 & REST API',
      borderGradient: 'from-teal-400 via-emerald-500 via-cyan-500 to-teal-400',
      iconColor: 'from-teal-600 to-emerald-500',
      shadowColor: 'shadow-teal-500/15',
      description: 'Extend your website\'s functionality with custom plugins or seamless integration of trusted third-party solutions and external API endpoints.',
      features: ['Bespoke Business Logic Plugins', 'CRM & ERP API Connectors', 'Secure Custom DB Tables']
    },
    {
      icon: Sliders,
      title: 'CMS Customization & Dashboards',
      badge: 'Tailored Control',
      borderGradient: 'from-yellow-400 via-amber-500 via-orange-500 to-yellow-400',
      iconColor: 'from-amber-500 to-orange-600',
      shadowColor: 'shadow-amber-500/15',
      description: 'Create custom dashboards, content workflows, user roles, forms, reports, and business-specific features that simplify daily website management.',
      features: ['Granular Role-Based Access', 'Custom ACF Admin Panels', 'Automated Content Workflows']
    },
    {
      icon: RefreshCw,
      title: 'Website Redesign & Migration',
      badge: 'Zero SEO Loss',
      borderGradient: 'from-indigo-500 via-purple-500 via-blue-600 to-indigo-500',
      iconColor: 'from-indigo-600 to-blue-500',
      shadowColor: 'shadow-indigo-500/15',
      description: 'Transform outdated websites into modern, mobile-friendly platforms or migrate safely from another CMS without losing valuable data or SEO rankings.',
      features: ['301 Redirect Mapping', 'Database & Media Transfer', 'Zero Downtime Migration']
    },
    {
      icon: Zap,
      title: 'Performance Optimization',
      badge: '98+ PageSpeed',
      borderGradient: 'from-emerald-400 via-green-500 via-teal-600 to-emerald-400',
      iconColor: 'from-emerald-600 to-teal-500',
      shadowColor: 'shadow-emerald-500/15',
      description: 'Improve loading speed, Core Web Vitals, caching, image optimization, database performance, and overall website responsiveness.',
      features: ['Redis Object Caching', 'Cloudflare WAF & CDN Sync', 'Core Web Vitals Pass']
    },
    {
      icon: Search,
      title: 'SEO-Friendly Development',
      badge: 'Organic Traffic',
      borderGradient: 'from-rose-400 via-pink-500 via-purple-600 to-rose-400',
      iconColor: 'from-rose-600 to-pink-500',
      shadowColor: 'shadow-rose-500/15',
      description: 'Build websites following technical SEO best practices, structured schema markup, and clean semantic code to improve search engine visibility.',
      features: ['Schema.org JSON-LD Sync', 'XML Sitemap Automation', 'Semantic HTML5 Markup']
    },
    {
      icon: ShieldCheck,
      title: 'Website Security & Maintenance',
      badge: '24/7 Managed Support',
      borderGradient: 'from-sky-400 via-cyan-500 via-indigo-600 to-sky-400',
      iconColor: 'from-cyan-600 to-blue-700',
      shadowColor: 'shadow-cyan-500/15',
      description: 'Protect your website with security hardening, malware protection, regular backups, updates, monitoring, and ongoing technical support.',
      features: ['Automated Hourly Backups', 'Real-Time Malware Scanning', 'Core & Plugin Patching']
    }
  ];

  // WooCommerce Features (Categorized into 4 Core Operational Modules)
  const ecommerceModules = {
    catalog: {
      id: 'catalog',
      label: 'Catalog & Storefront',
      icon: Layers,
      color: 'from-orange-500 to-amber-500',
      summary: 'Engineered for high product velocity, structured taxonomies, and instant catalog search responsiveness.',
      features: [
        { icon: Lock, title: 'Secure Online Store', desc: 'Bank-grade SSL encryption, PCI-DSS compliance, and hardened checkout endpoints.', step: '01' },
        { icon: Layers, title: 'Unlimited Products & Categories', desc: 'Structure complex catalogs with nested categories, attributes, and tags.', step: '02' },
        { icon: Sliders, title: 'Product Variations & Attributes', desc: 'Support size, color, material, and custom configurable options effortlessly.', step: '03' },
        { icon: Search, title: 'Wishlist & Product Comparison', desc: 'Empower shoppers to bookmark favorites and evaluate specifications side-by-side.', step: '04' }
      ]
    },
    payments: {
      id: 'payments',
      label: 'Payments & Checkout',
      icon: Zap,
      color: 'from-cyan-500 to-blue-600',
      summary: 'Optimized for high-conversion multi-gateway checkout, automated tax calculation, and instant invoicing.',
      features: [
        { icon: Zap, title: 'Multiple Payment Gateways', desc: 'Stripe, PayPal, Razorpay, UPI, Cash on Delivery, and subscription billing engines.', step: '05' },
        { icon: Globe, title: 'Shipping & Tax Configuration', desc: 'Automated weight-based shipping rates, zone taxes, and courier API sync.', step: '06' },
        { icon: Sparkles, title: 'Coupon & Discount Management', desc: 'Flexible promotional codes, bulk tier discounts, and seasonal sale triggers.', step: '07' },
        { icon: FileText, title: 'GST & Invoice Ready', desc: 'Automated GST tax calculation, PDF invoice generation, and tax filing reports.', step: '08' }
      ]
    },
    operations: {
      id: 'operations',
      label: 'Operations & Fulfillment',
      icon: Database,
      color: 'from-emerald-500 to-teal-600',
      summary: 'Real-time stock level synchronization, self-service portals, and automated WhatsApp/email notifications.',
      features: [
        { icon: Database, title: 'Inventory & Stock Management', desc: 'Real-time stock level updates, low-stock alerts, and warehouse backorder support.', step: '09' },
        { icon: Users, title: 'Customer Accounts & Portals', desc: 'Self-service account dashboards, order history, downloadable assets, and re-ordering.', step: '10' },
        { icon: Activity, title: 'Order Tracking & Notifications', desc: 'Automated WhatsApp & Email notifications with live courier tracking links.', step: '11' },
        { icon: Edit, title: 'Invoice & Packing Slip Gen', desc: 'Instant downloadable PDF invoices and printable warehouse packing slips.', step: '12' }
      ]
    },
    scaling: {
      id: 'scaling',
      label: 'Enterprise & Mobile PWA',
      icon: Cpu,
      color: 'from-purple-500 to-indigo-600',
      summary: 'Built for multi-vendor marketplaces, multi-currency international sales, and native-feeling PWA mobile shopping.',
      features: [
        { icon: Cpu, title: 'Analytics & Sales Reports', desc: 'Comprehensive sales dashboards, top product metrics, and revenue trends.', step: '13' },
        { icon: Building2, title: 'Multi-Vendor Support (Optional)', desc: 'Transform your store into a multi-seller platform like Amazon or Etsy with commission split.', step: '14' },
        { icon: Globe, title: 'Multi-Language & Multi-Currency', desc: 'Geo-location currency conversion and WPML/Polylang translation pipelines.', step: '15' },
        { icon: Code2, title: 'Mobile-Optimized Experience', desc: 'Ultra-fast mobile PWA shopping experience tuned for high touch conversions.', step: '16' }
      ]
    }
  };

  // Why Choose HOASP (10 Reasons)
  const whyChooseList = [
    'Experienced WordPress & WooCommerce Developers',
    'Fully Responsive & Mobile-First Design',
    'SEO-Friendly Architecture & Clean Semantic Code',
    'Fast Loading & Performance Optimized (98+ PageSpeed)',
    'Secure Development Practices & Hardened DB',
    'Scalable & Future-Ready Headless Architecture',
    'Easy-to-Manage Content Management System',
    'Custom Features Based on Business Requirements',
    'Transparent Development Process & Timelines',
    'Dedicated Post-Launch 24/7 Technical Support'
  ];

  // Tech Stack Chips
  const techStack = [
    'WordPress CMS',
    'WooCommerce',
    'PHP 8.3',
    'HTML5',
    'CSS3',
    'JavaScript (ES6+)',
    'Bootstrap 5',
    'React (Headless WP)',
    'MySQL',
    'REST API & GraphQL',
    'Elementor Pro',
    'WPBakery',
    'Advanced Custom Fields (ACF)'
  ];


  return (
    <div className="min-h-screen text-left pt-20 text-gray-100 relative bg-black overflow-hidden">

      {/* GoNoise Master Series Scroll-Driven Motion Canvas Backdrop (Electric Blue & Royal Purple Tones) */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-black">
        
        {/* Subtle Tech Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#38bdf815_1px,transparent_1px),linear-gradient(to_bottom,#38bdf815_1px,transparent_1px)] bg-[size:4rem_4rem]" />

        {/* Dynamic Motion Blob 1 (Electric Blue & Indigo) */}
        <div
          className="absolute -top-28 -left-28 w-[850px] h-[850px] bg-gradient-to-br from-blue-600/45 via-indigo-600/35 to-transparent rounded-full blur-[90px] transition-transform duration-100 ease-out"
          style={{
            transform: `translate3d(${scrollY * 0.12}px, ${scrollY * 0.18}px, 0) scale(${1 + (scrollY % 600) * 0.0004})`
          }}
        />

        {/* Dynamic Motion Blob 2 (Electric Cyan & Deep Sky) */}
        <div
          className="absolute top-1/3 -right-28 w-[900px] h-[900px] bg-gradient-to-bl from-cyan-400/45 via-blue-500/35 to-transparent rounded-full blur-[95px] transition-transform duration-100 ease-out"
          style={{
            transform: `translate3d(${-scrollY * 0.1}px, ${-scrollY * 0.14}px, 0) rotate(${scrollY * 0.04}deg)`
          }}
        />

        {/* Dynamic Motion Blob 3 (Royal Purple & Violet) */}
        <div
          className="absolute bottom-10 left-1/4 w-[850px] h-[850px] bg-gradient-to-tr from-purple-600/45 via-violet-500/35 to-transparent rounded-full blur-[95px] transition-transform duration-100 ease-out"
          style={{
            transform: `translate3d(${scrollY * 0.08}px, ${-scrollY * 0.12}px, 0) scale(${1 + (scrollY % 900) * 0.0003})`
          }}
        />

        {/* Deep Blue & Purple Radial Center Shimmer Ray */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[1100px] bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-purple-600/25 via-blue-500/18 to-transparent rounded-full blur-3xl transition-transform duration-150 ease-out pointer-events-none"
          style={{
            transform: `translate(-50%, -50%) rotate(${scrollY * 0.06}deg) scale(${1 + (scrollY % 400) * 0.0002})`
          }}
        />

        {/* Vignette Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70 pointer-events-none" />
      </div>

      {/* Hero Showcase Section with Centered Layout & Left/Right Ambient Light Gradients */}
      <section className="py-24 bg-black/40 backdrop-blur-md relative overflow-hidden border-b border-gray-800/80 z-10">

        {/* Left Light Gradient Orb */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-tr from-cyan-500/20 via-blue-500/10 to-transparent rounded-full blur-3xl pointer-events-none -translate-x-1/2" />

        {/* Right Light Gradient Orb */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-tl from-orange-500/20 via-amber-500/10 to-transparent rounded-full blur-3xl pointer-events-none translate-x-1/2" />

        <div className="container mx-auto px-6 relative z-10 text-center">
          <div className="max-w-4xl mx-auto flex flex-col items-center">

            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="w-4 h-4 text-orange-400" />
              <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                WordPress CMS & E-Commerce Engineering
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight max-w-3xl">
              Build a Powerful Website That <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Grows Your Business</span>
            </h1>

            {/* Main Sub-description */}
            <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-8 max-w-2xl">
              We engineer fast, secure, and scalable <strong className="text-orange-400">WordPress CMS</strong> & <strong className="text-cyan-400">WooCommerce</strong> solutions—from corporate portals to high-volume online stores tailored to your business growth.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/contact">
                <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all duration-300 flex items-center gap-2">
                  Start Your WordPress Project
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </a>
              <a href="#cms-services">
                <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800 px-8 py-6 text-base font-semibold rounded-xl">
                  Explore Our Services
                </Button>
              </a>
            </div>

          </div>
        </div>
      </section>

      {/* Performance SLA Metrics Section with Light Background Image & White Motion Cards */}
      <section className="py-14 bg-[url('https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1600&auto=format&fit=crop')] bg-cover bg-center bg-slate-100/90 bg-blend-overlay border-y border-gray-200 shadow-md relative z-10">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">

            {/* Metric 1 - PageSpeed */}
            <div className="p-[2px] rounded-2xl bg-gradient-to-r from-cyan-400 via-sky-500 via-blue-600 to-cyan-400 animate-gradient-border shadow-xl shadow-cyan-500/10 hover:scale-[1.03] transition-transform duration-300">
              <div className="p-6 rounded-[14px] bg-white h-full flex flex-col justify-center items-center shadow-sm">
                <div className="text-3xl md:text-4xl font-extrabold text-cyan-600 mb-1">98+</div>
                <div className="text-xs md:text-sm text-slate-900 font-bold uppercase tracking-wider">Google PageSpeed Score</div>
              </div>
            </div>

            {/* Metric 2 - Sub-Second Load Time */}
            <div className="p-[2px] rounded-2xl bg-gradient-to-r from-amber-400 via-orange-500 via-red-500 to-amber-400 animate-gradient-border shadow-xl shadow-orange-500/10 hover:scale-[1.03] transition-transform duration-300">
              <div className="p-6 rounded-[14px] bg-white h-full flex flex-col justify-center items-center shadow-sm">
                <div className="text-3xl md:text-4xl font-extrabold text-orange-600 mb-1">&lt; 400ms</div>
                <div className="text-xs md:text-sm text-slate-900 font-bold uppercase tracking-wider">Sub-Second Load Time</div>
              </div>
            </div>

            {/* Metric 3 - Gutenberg & Drupal Code */}
            <div className="p-[2px] rounded-2xl bg-gradient-to-r from-blue-500 via-indigo-500 via-purple-500 to-blue-500 animate-gradient-border shadow-xl shadow-blue-500/10 hover:scale-[1.03] transition-transform duration-300">
              <div className="p-6 rounded-[14px] bg-white h-full flex flex-col justify-center items-center shadow-sm">
                <div className="text-3xl md:text-4xl font-extrabold text-blue-600 mb-1">100%</div>
                <div className="text-xs md:text-sm text-slate-900 font-bold uppercase tracking-wider">Custom Gutenberg & Drupal Code</div>
              </div>
            </div>

            {/* Metric 4 - Security & Backup Monitoring */}
            <div className="p-[2px] rounded-2xl bg-gradient-to-r from-emerald-400 via-teal-500 via-cyan-500 to-emerald-400 animate-gradient-border shadow-xl shadow-emerald-500/10 hover:scale-[1.03] transition-transform duration-300">
              <div className="p-6 rounded-[14px] bg-white h-full flex flex-col justify-center items-center shadow-sm">
                <div className="text-3xl md:text-4xl font-extrabold text-emerald-600 mb-1">24/7</div>
                <div className="text-xs md:text-sm text-slate-900 font-bold uppercase tracking-wider">Security & Backup Monitoring</div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* WordPress Development Services Grid (Translucent Glassmorphism over Fixed BG) */}
      <section id="cms-services" className="py-24 bg-black/45 backdrop-blur-md border-t border-gray-800 text-left relative z-10">
        <div className="container mx-auto px-6 relative z-10">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                Our Core Services
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Our WordPress <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Development Services</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              Tailored WordPress and WooCommerce engineering designed for enterprise speed, security, and high content velocity.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesList.map((service, idx) => (
              <div
                key={idx}
                className={`p-[2px] rounded-2xl bg-gradient-to-r ${service.borderGradient} animate-gradient-border shadow-lg ${service.shadowColor} hover:scale-[1.02] transition-transform duration-300 flex flex-col`}
              >
                <div className="bg-white rounded-[14px] p-8 h-full flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div className={`w-14 h-14 bg-gradient-to-br ${service.iconColor} rounded-xl flex items-center justify-center shadow-md`}>
                        <service.icon className="w-7 h-7 text-white" />
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-800 bg-cyan-50 border border-cyan-200 px-2.5 py-1 rounded-full">
                        {service.badge}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {service.title}
                    </h3>

                    <p className="text-gray-600 text-sm leading-relaxed mb-6">
                      {service.description}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-gray-100 space-y-2">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center text-sm text-gray-700 font-medium">
                        <CheckCircle2 className="w-4 h-4 text-orange-600 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* WooCommerce Features Showcase (Categorized 4-Tab Module Showcase) */}
      <section className="py-24 bg-black/50 backdrop-blur-md border-t border-gray-800 text-left relative overflow-hidden z-10">

        {/* Background Ambient Glow Orbs */}
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-6 relative z-10">

          {/* Header */}
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="inline-block bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-4 shadow-sm">
              <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                E-Commerce Suite Architecture
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              WooCommerce <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Store Modules</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg">
              Explore our 16 core e-commerce capabilities organized into interactive operational modules.
            </p>
          </div>

          {/* Interactive 4-Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {Object.values(ecommerceModules).map((mod) => {
              const Icon = mod.icon;
              const isActive = activeEcommerceTab === mod.id;

              if (isActive) {
                return (
                  <button
                    key={mod.id}
                    onClick={() => setActiveEcommerceTab(mod.id)}
                    className="flex items-center space-x-2.5 px-6 py-3.5 rounded-xl font-bold text-sm bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/25 scale-105 transition-all duration-300 cursor-pointer"
                  >
                    <Icon className="w-4 h-4 text-white" />
                    <span>{mod.label}</span>
                  </button>
                );
              }

              return (
                <div
                  key={mod.id}
                  className={`p-[2px] rounded-xl bg-gradient-to-r ${
                    mod.id === 'catalog'
                      ? 'from-orange-400 via-amber-400 via-yellow-400 to-orange-400'
                      : mod.id === 'payments'
                      ? 'from-cyan-400 via-sky-500 via-blue-500 to-cyan-400'
                      : mod.id === 'operations'
                      ? 'from-emerald-400 via-teal-400 via-cyan-400 to-emerald-400'
                      : 'from-purple-400 via-pink-500 via-indigo-400 to-purple-400'
                  } animate-gradient-border hover:scale-105 transition-all duration-300`}
                >
                  <button
                    onClick={() => setActiveEcommerceTab(mod.id)}
                    className="w-full h-full px-6 py-3 rounded-[10px] bg-gray-950 text-gray-200 hover:text-white font-bold text-sm flex items-center space-x-2.5 cursor-pointer transition-colors"
                  >
                    <Icon className="w-4 h-4 text-orange-400" />
                    <span>{mod.label}</span>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Active Module Summary Banner */}
          <div className="bg-gray-900/90 border border-gray-800 rounded-2xl p-4 mb-8 max-w-4xl mx-auto flex items-center justify-center gap-4 backdrop-blur-sm">
            <div className="flex items-center space-x-3 text-center">
              <div className="w-2.5 h-2.5 rounded-full bg-orange-400 animate-pulse flex-shrink-0" />
              <p className="text-xs md:text-sm text-gray-300 font-medium">
                {ecommerceModules[activeEcommerceTab].summary}
              </p>
            </div>
          </div>

          {/* 4 Feature Cards Grid for Active Module */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ecommerceModules[activeEcommerceTab].features.map((item, idx) => {
              const FeatureIcon = item.icon;
              return (
                <div
                  key={idx}
                  className="group relative bg-white border border-gray-200/90 rounded-2xl p-6 shadow-sm hover:shadow-xl hover:border-orange-400 hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between overflow-hidden text-left cursor-pointer"
                >
                  {/* Top Corner Orange Accent Blur on Hover */}
                  <div className="absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br from-orange-400/10 to-cyan-400/10 rounded-full blur-lg group-hover:scale-150 transition-transform duration-500 pointer-events-none" />

                  <div>
                    {/* Header Row: Icon Badge & Feature Step Pill */}
                    <div className="flex items-center justify-between mb-5">
                      <div className="w-12 h-12 rounded-2xl bg-orange-500/10 border border-orange-500/20 text-orange-600 group-hover:bg-gradient-to-r group-hover:from-orange-500 group-hover:to-amber-500 group-hover:text-white group-hover:border-transparent transition-all duration-300 flex items-center justify-center shadow-sm">
                        <FeatureIcon className="w-6 h-6" />
                      </div>
                      <span className="text-xs font-mono font-bold text-orange-600 bg-orange-50 border border-orange-200 px-2.5 py-1 rounded-full">
                        FEATURE {item.step}
                      </span>
                    </div>

                    {/* Title */}
                    <h4 className="text-base font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                      {item.title}
                    </h4>

                    {/* Description */}
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>

                  {/* Bottom Orange to Cyan Gradient Progress Bar on Hover */}
                  <div className="mt-5 pt-3 border-t border-gray-100 relative">
                    <div className="h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-orange-500 via-amber-400 to-cyan-500 transition-all duration-500 ease-out rounded-full" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose HOASP Section */}
      <section className="py-24 bg-black/60 backdrop-blur-md border-t border-gray-800 relative z-10">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="inline-block bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-amber-400 text-sm font-semibold uppercase tracking-wider">
                Proven Excellence
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6">
              Why Choose <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-cyan-400 bg-clip-text text-transparent">HOASP?</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {whyChooseList.map((reason, idx) => (
              <div key={idx} className="bg-gray-900/80 border border-gray-800 p-5 rounded-2xl flex items-start space-x-3.5 hover:border-amber-500/40 transition-all text-left">
                <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-200 font-medium">{reason}</span>
              </div>
            ))}
          </div>
        </div>
      </section>



    </div>
  );
};

export default CMSSolution;